import type { Schema } from '@dealdesk/api-schema';
import { useTranslation } from 'react-i18next';

import { Button, DownloadIcon, EyeIcon, toast } from '@/design-system';
import { apiErrorMessage } from '@/shared/api';
import { isViewable, useOpenFile, type Disposition, type DownloadLink, type FileOpenMode } from '@/shared/files';

export interface FileActionsProps {
  fileName: string;
  contentType?: string | null;
  /** Fetches a short-lived link (the owning feature's api function). */
  fetchLink: (disposition: Disposition) => Promise<DownloadLink>;
  /** Scan state when known: pending → disabled with the reason; infected / failed → no actions. */
  scanStatus?: Schema<'FileScanStatus'> | null;
  /** Hide the "View" button (e.g. compact rows); Download stays. */
  hideView?: boolean;
}

/** View (opens PDF / images in a new tab) and Download for one stored file. Only clean files are served. */
export function FileActions({ fileName, contentType, fetchLink, scanStatus, hideView }: FileActionsProps) {
  const { t } = useTranslation();
  const open = useOpenFile();
  if (scanStatus === 'infected' || scanStatus === 'failed') return null;
  const scanning = scanStatus === 'pending';
  const title = scanning ? t('files.scanningHint') : undefined;
  const run = (mode: FileOpenMode) =>
    open.mutate(
      { key: `${fileName}:${mode}`, fetchLink, mode },
      { onError: (e) => void toast.error(apiErrorMessage(e)) },
    );
  const pendingMode = open.isPending ? open.variables.mode : null;
  return (
    <span className="inline-flex items-center gap-1">
      {!hideView && isViewable({ contentType: contentType ?? null, fileName }) ? (
        <Button size="xs" variant="ghost" icon={<EyeIcon size={13} />} disabled={scanning} title={title} loading={pendingMode === 'view'} aria-label={t('files.viewLabel', { name: fileName })} onClick={() => run('view')}>
          {t('files.view')}
        </Button>
      ) : null}
      <Button size="xs" variant="ghost" icon={<DownloadIcon size={13} />} disabled={scanning} title={title} loading={pendingMode === 'download'} aria-label={t('files.downloadLabel', { name: fileName })} onClick={() => run('download')}>
        {t('files.download')}
      </Button>
    </span>
  );
}
